import React, { useMemo, useRef, useState } from "react";
import { Copy, Loader2, Share2 } from "lucide-react";
import { useLocale } from "@/context/LocaleContext";
import { bettorDisplayName } from "@/lib/bettorDisplayName";
import { matchPairBroadcast } from "@/lib/broadcastTeamCode";
import {
  formatBroadcastMatchdayLabel,
  fixturesOnBroadcastMatchday,
  resolveActiveBroadcastMatchday,
} from "@/lib/broadcastMatchday";
import { parseKickoffUtcMs, kickoffToDate } from "@/lib/kickoffTime";
import { MatrixTable, OverviewSection } from "@/components/LeaderboardMatrixTable";

function byKickoffAsc(a, b) {
  const ta = parseKickoffUtcMs(a.kickoff) ?? Infinity;
  const tb = parseKickoffUtcMs(b.kickoff) ?? Infinity;
  if (ta !== tb) return ta - tb;
  return String(a.id ?? "").localeCompare(String(b.id ?? ""));
}

function formatPairBet(b) {
  if (!b) return "–";
  const h = b.home_score;
  const a = b.away_score;
  if (h == null && a == null) return "–";
  return `${h ?? "–"}–${a ?? "–"}`;
}

function CopyToolbar({ exportRef, title, t }) {
  const [copyBusy, setCopyBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const busy = copyBusy || shareBusy;

  const onCopy = async () => {
    try {
      setCopyBusy(true);
      const { copyNodeImageToClipboard } = await import("@/lib/chartCapture");
      const { toast } = await import("@/hooks/use-toast");
      await copyNodeImageToClipboard(exportRef.current, { pixelRatio: 2 });
      toast({ title: t("common.copied") });
    } catch {
      const { toast } = await import("@/hooks/use-toast");
      toast({ title: t("common.copyImageFailed") });
    } finally {
      setCopyBusy(false);
    }
  };

  const onShare = async () => {
    try {
      setShareBusy(true);
      const { shareNodeImage } = await import("@/lib/chartCapture");
      const { toast } = await import("@/hooks/use-toast");
      const result = await shareNodeImage(exportRef.current, {
        title,
        filename: "matchdag-tips.png",
        pixelRatio: 2,
      });
      if (result === "downloaded") {
        toast({ title: t("common.shareDownloaded") });
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      const { toast } = await import("@/hooks/use-toast");
      toast({ title: t("common.shareImageFailed") });
    } finally {
      setShareBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        disabled={busy}
        onClick={onCopy}
        className="chart-toolbar-icon-btn"
        title={t("lb.pickMatrix.copyMatchdayPicks")}
        aria-label={t("lb.pickMatrix.copyMatchdayPicks")}
        data-testid="lb-copy-matchday-picks"
      >
        {copyBusy ? (
          <Loader2 className="chart-toolbar-icon-btn__icon animate-spin" />
        ) : (
          <Copy className="chart-toolbar-icon-btn__icon" />
        )}
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={onShare}
        className="chart-toolbar-icon-btn"
        title={t("common.shareImage")}
        aria-label={t("common.shareImage")}
      >
        {shareBusy ? (
          <Loader2 className="chart-toolbar-icon-btn__icon animate-spin" />
        ) : (
          <Share2 className="chart-toolbar-icon-btn__icon" />
        )}
      </button>
    </div>
  );
}

/** Transposed layout for image export: one row per participant, one column per match. */
function MatchdayCaptureTable({ rowLabel, matchColumns, rows, tableClassName = "" }) {
  return (
    <table className={`w-max text-sm font-mono ${tableClassName}`.trim()}>
      <thead>
        <tr className="text-[var(--text-dim)]">
          <th className="px-2 pt-2 pb-0 text-left font-normal min-w-[4.5rem]" aria-hidden />
          {matchColumns.map((col) => (
            <th
              key={`${col.key}-time`}
              className="px-2 pt-2 pb-0 text-center text-[10px] font-normal min-w-[4rem] whitespace-nowrap leading-none"
            >
              {col.time}
            </th>
          ))}
        </tr>
        <tr className="border-b border-[var(--line)] text-[var(--text-dim)]">
          <th className="px-2 pt-1 pb-2 text-left font-normal min-w-[4.5rem] align-bottom">{rowLabel}</th>
          {matchColumns.map((col) => (
            <th
              key={col.key}
              className="px-2 pt-1 pb-2 text-center font-normal min-w-[4rem] whitespace-nowrap align-bottom"
            >
              {col.pair}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((cells, idx) => (
          <tr key={idx} className="border-t border-[var(--line)]/70 odd:bg-[var(--bg)]/30">
            <td className="px-2 py-1.5 font-medium text-[var(--accent-2)] whitespace-nowrap">{cells[0]}</td>
            {cells.slice(1).map((c, i) => (
              <td key={i} className="px-2 py-1.5 text-center tabular-nums text-[var(--text-dim)]">
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Copyable image: today's broadcast matchday + all participants' score picks. */
export default function LeaderboardMatchdayPicksExport({ project, picksMatrix }) {
  const { lang, t } = useLocale();
  const exportRef = useRef(null);
  const loc = lang === "en" ? "en-GB" : "sv-SE";
  const fixtures = useMemo(() => project?.fixtures ?? [], [project]);
  const members = useMemo(() => picksMatrix?.members ?? [], [picksMatrix]);
  const mbU = useMemo(() => picksMatrix?.match_bets_by_user ?? {}, [picksMatrix]);

  const matchdayIso = useMemo(() => resolveActiveBroadcastMatchday(fixtures), [fixtures]);
  const dayMatches = useMemo(() => {
    const ms = fixturesOnBroadcastMatchday(fixtures, matchdayIso);
    return [...ms].sort(byKickoffAsc);
  }, [fixtures, matchdayIso]);

  const dateLabel = useMemo(
    () => formatBroadcastMatchdayLabel(matchdayIso, lang),
    [matchdayIso, lang],
  );
  const title = useMemo(
    () => t("lb.pickMatrix.matchdayTitle", { date: dateLabel }),
    [t, dateLabel],
  );

  const rows = useMemo(() => {
    return dayMatches.map((m) => {
      const mid = m.id != null ? String(m.id) : m.id;
      return [
        matchPairBroadcast(m),
        ...members.map((u) => {
          const b = mbU[u.id]?.[mid] ?? mbU[u.id]?.[m.id];
          return formatPairBet(b);
        }),
      ];
    });
  }, [dayMatches, members, mbU]);

  const matchColumns = useMemo(
    () =>
      dayMatches.map((m) => {
        const ko = kickoffToDate(m.kickoff);
        const timeStr = Number.isFinite(ko.getTime())
          ? ko.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" })
          : "–";
        const pair = matchPairBroadcast(m);
        return {
          key: m.id != null ? String(m.id) : pair,
          time: timeStr,
          pair,
        };
      }),
    [dayMatches, loc],
  );

  const transposedRows = useMemo(() => {
    return members.map((u) => [
      bettorDisplayName(u.name),
      ...dayMatches.map((m) => {
        const mid = m.id != null ? String(m.id) : m.id;
        const b = mbU[u.id]?.[mid] ?? mbU[u.id]?.[m.id];
        return formatPairBet(b);
      }),
    ]);
  }, [dayMatches, members, mbU]);

  if (!picksMatrix || !matchdayIso || dayMatches.length === 0 || members.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <CopyToolbar exportRef={exportRef} title={title} t={t} />
      <OverviewSection title={title}>
        <MatrixTable
          rowLabel={t("lb.pickMatrix.colMatch")}
          members={members}
          rows={rows}
          leaderboardRows={[]}
          breakdownKey={null}
          footerRows={[]}
        />
      </OverviewSection>
      {/* Hidden in-place layout for copy/share (transposed; keeps measurable dimensions). */}
      <div
        ref={exportRef}
        className="chart-capture-root pointer-events-none invisible fixed left-0 top-0 -z-50 w-max space-y-3 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3 sm:p-4 text-[var(--text)]"
        aria-hidden
      >
        <h4 className="chart-capture-title text-center font-display text-base leading-snug text-[var(--accent)] sm:text-lg">
          {title}
        </h4>
        <MatchdayCaptureTable
          rowLabel={t("lb.pickMatrix.colMatchdayPlayer")}
          matchColumns={matchColumns}
          rows={transposedRows}
          tableClassName="chart-capture-table"
        />
        {project?.name ? (
          <p className="chart-capture-muted text-center text-[11px] text-[var(--text-dim)] sm:text-xs">
            {project.name}
          </p>
        ) : null}
      </div>
    </div>
  );
}
